import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Star, Loader2, CheckCircle2, Heart } from "lucide-react";
import { api } from "../lib/api";
import { Container } from "../components/site/Reveal";
import { useSite } from "../context/SiteContext";

const Review = () => {
  const { token } = useParams();
  const { t } = useSite();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [form, setForm] = useState({ text: "", name: "", location: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Leave a review | StripWise";
    api.get(`/reviews/request/${token}`).then((r) => { setInfo(r.data); setForm((f) => ({ ...f, name: r.data.name })); setDone(r.data.submitted); })
      .catch((e) => setErr(e?.response?.data?.detail || "Invalid link"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/reviews/request/${token}`, { rating, ...form });
      setDone(true);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Could not submit");
    } finally {
      setBusy(false);
    }
  };

  const input = "w-full rounded-xl border border-[#dcebe8] bg-white px-4 py-3 text-[15px] outline-none focus:border-[#0e8c7f] focus:ring-2 focus:ring-[#0e8c7f]/15";
  return (
    <section className="min-h-[70vh] bg-[linear-gradient(180deg,#eefbf8,#ffffff)] py-14 md:py-20" data-testid="review-page">
      <Container className="max-w-[620px] lg:w-[50%]">
        {err && !info ? (
          <div className="rounded-2xl bg-white p-8 text-center text-[#4b5d5a]">{err}<div className="mt-4"><Link to="/" className="sw-btn-primary">{t("Go to homepage")}</Link></div></div>
        ) : !info ? (
          <Loader2 className="mx-auto animate-spin text-[#0e8c7f]" />
        ) : done ? (
          <div className="rounded-[24px] border border-[#e0f0ec] bg-white p-10 text-center sw-fade-up" data-testid="review-thanks">
            <CheckCircle2 size={44} className="mx-auto text-[#0e8c7f]" />
            <h1 className="mt-4 text-[26px] font-bold text-[#0f2e2a]">{t("Thank you{name}!", { name: info.name ? `, ${info.name}` : "" })}</h1>
            <p className="mt-2 text-[16px] text-[#4b5d5a]">{t("Your review helps other sellers find a buyer they can trust.")}</p>
            <Link to="/" className="sw-btn-outline mt-6">{t("Back to StripWise")}</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-[24px] border border-[#e0f0ec] bg-white p-7 md:p-9 shadow-[0_30px_60px_-40px_rgba(15,46,42,.35)] sw-fade-up" data-testid="review-form">
            <Heart size={28} className="text-[#f59e0b]" />
            <h1 className="mt-3 text-[28px] font-bold text-[#0f2e2a]">{t("How did we do{name}?", { name: info.name ? `, ${info.name}` : "" })}</h1>
            <p className="mt-1.5 text-[15.5px] text-[#5b6d6a]">{t("Rate your experience selling with StripWise.")}</p>
            <div className="mt-6 flex gap-1.5" onMouseLeave={() => setHover(0)}>
              {[1, 2, 3, 4, 5].map((i) => (
                <button type="button" key={i} onClick={() => setRating(i)} onMouseEnter={() => setHover(i)} className="text-[#f59e0b] transition-transform hover:scale-110" aria-label={`${i} stars`} data-testid={`review-star-${i}`}>
                  <Star size={38} fill={i <= (hover || rating) ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
            <textarea className={`${input} mt-6 min-h-[120px]`} placeholder={t("What was it like selling to us?")} value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} required data-testid="review-text-input" />
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input className={input} placeholder={t("Name to show (e.g. Maria G.)")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="review-name-input" />
              <input className={input} placeholder={t("City, State (e.g. Houston, TX)")} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="review-location-input" />
            </div>
            {err && <p className="mt-3 text-[14px] text-red-600">{err}</p>}
            <button disabled={busy} className="sw-btn-primary mt-6 w-full disabled:opacity-60" data-testid="review-submit">{busy && <Loader2 size={16} className="animate-spin" />}{t("Submit review")}</button>
          </form>
        )}
      </Container>
    </section>
  );
};

export default Review;
